import { Locale, fromUnixTime } from 'date-fns';
import { format, formatInTimeZone } from 'date-fns-tz';

type TimeUnit = 'days' | 'hours' | 'minutes' | 'seconds';

/**
 * Formats Unix timestamp to datetime string.
 * @param value Unix timestamp
 * @param formatString String representation of output datetime string
 * @param formatOptions
 * @param options Options to pass to date-fns format method
 * @returns Formatted datetime string
 */
export const formatTimestamp = (
    value: Date | number | string,
    formatString: string = 'dd.MM.yyyy HH:mm',
    formatOptions?: {
        timeZone?: string;
        addHours?: number;
    },
    options?: {
        locale?: Locale;
        weekStartsOn?: 0 | 1 | 2 | 3 | 4 | 5 | 6;
        firstWeekContainsDate?: 1 | 4;
        useAdditionalWeekYearTokens?: boolean;
        useAdditionalDayOfYearTokens?: boolean;
    }
): string => {
    if (formatOptions?.timeZone) {
        return formatInTimeZone(value, formatOptions.timeZone, formatString, options);
    }

    const baseDate = typeof value === 'number' ? fromUnixTime(value) : new Date(value);
    const offsetMilliseconds = (formatOptions?.addHours || 0) * 60 * 60 * 1000;
    const dateValue = new Date(baseDate.getTime() + offsetMilliseconds);

    return format(dateValue, formatString, options);
};

/**
 * Appends the given local time to the date and converts it to UTC.
 * @param value Date value
 * @param time Time string in 'HH:mm' format
 * @returns Formatted datetime string in UTC
 */
export const appendTimeAndConvertToUtc = (value: Date, time: string): string => {
    const [hours, minutes] = time.split(':').map(Number);
    const date = new Date(value.getFullYear(), value.getMonth(), value.getDate(), hours, minutes);

    // Convert local date to UTC
    const utcDate = new Date(date.getTime() + date.getTimezoneOffset() * 60000);

    return format(utcDate, 'dd.MM.yyyy HH:mm', { timeZone: 'UTC' });
};

/**
 * Compares whether the given Unix timestamp is within a certain time period from now.
 * @param orderAt Unix timestamp (in seconds)
 * @param maxTime Maximum time allowed
 * @param unit Time unit ('days', 'hours', 'minutes', 'seconds')
 * @param now Optional override of current time (for testing)
 * @returns True if within threshold, false otherwise
 */
const isWithinTimeFromNow = (orderAt: number, maxTime: number, unit: TimeUnit, now: number = Date.now()): boolean => {
    const orderDate = fromUnixTime(orderAt);
    const diffInMs = now - orderDate.getTime();

    const divisors = {
        seconds: 1000,
        minutes: 1000 * 60,
        hours: 1000 * 60 * 60,
        days: 1000 * 60 * 60 * 24
    };

    const diffInUnit = diffInMs / divisors[unit];
    return diffInUnit <= maxTime;
};

/**
 * Compares whether the given Unix timestamp is within a certain number of days from now.
 * @param orderAt Unix timestamp (in seconds)
 * @param maxDays Max number of days allowed
 * @param now Optional override of current time (for testing)
 * @returns True if within threshold, false otherwise
 */
export const isWithinDaysFromNow = (orderAt: number, maxDays: number, now: number = Date.now()): boolean => {
    return isWithinTimeFromNow(orderAt, maxDays, 'days', now);
};

/**
 * Compares whether the given Unix timestamp is within a certain number of hours from now.
 * @param orderAt Unix timestamp (in seconds)
 * @param maxHours Max number of hours allowed
 * @param now Optional override of current time (for testing)
 * @returns True if within a threshold, false otherwise
 */
export const isWithinHoursFromNow = (orderAt: number, maxHours: number, now: number = Date.now()): boolean => {
    return isWithinTimeFromNow(orderAt, maxHours, 'hours', now);
};
